'use client'

import React, { useCallback, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'

import { domainCountry } from '@/src/constants/domain'
import useClevertapForLoginBanner from '@/src/hooks/analytics/clevertap/loginBanner'
import { selectAuth } from '@/src/redux/slices/authSlice'
import { useAppSelector } from '@/src/redux/store'
import { SignInPromoBannerConfig } from '@/src/types/signInPromoBanner'

import { SignInPromoBanner } from './signInPromoBanner'

const SignInModalWrapper = dynamic(
    () => import(/* webpackChunkName: "signin-modal" */ '@/src/components/common/header/variants/flyin/SignInModal/SignInModalWrapper'),
    { ssr: false }
)

const SIGN_IN_PROMO_BANNER_CONFIG_URL = 'https://ui.cltp.co/json/signInPromoBannerConfig.json'

export interface SignInPromoBannerWrapperProps {
    offerIndex?: number
}

const SignInPromoBannerWrapper: React.FC<SignInPromoBannerWrapperProps> = ({ offerIndex = 0 }) => {
    const t = useTranslations('itineraryPage.signInPromoBanner')
    const locale = useLocale()
    const { signedIn } = useAppSelector(selectAuth)
    const [signInModalOpen, setSignInModalOpen] = useState(false)
    const [config, setConfig] = useState<SignInPromoBannerConfig | null>(null)
    const { triggerLoginBannerClickEvent } = useClevertapForLoginBanner()

    useEffect(() => {
        fetch(SIGN_IN_PROMO_BANNER_CONFIG_URL)
            .then(res => res.json())
            .then((data: SignInPromoBannerConfig) => setConfig(data))
            .catch(() => {})
    }, [])

    const entries = config ? (config.markets[domainCountry] ?? config.markets[config.defaultMarket]) : null
    const offer = entries?.[offerIndex] ?? null

    const handleLoginClick = useCallback(() => {
        triggerLoginBannerClickEvent()
        setSignInModalOpen(true)
    }, [triggerLoginBannerClickEvent])

    const handleSignInModalClose = useCallback(() => {
        setSignInModalOpen(false)
    }, [])

    if (signedIn || !offer?.promoCode || !offer?.savingsDisplay) {
        return null
    }

    const isRtl = locale === 'ar'

    return (
        <>
            <Box sx={{ mb: 1, width: '100%' }}>
                <SignInPromoBanner
                    title={t('title', { savings: offer.savingsDisplay })}
                    subtitle={t('subtitle')}
                    codeLabel={t('codeLabel')}
                    promoCode={offer.promoCode}
                    ctaLabel={t('cta')}
                    isRtl={isRtl}
                    onLoginClick={handleLoginClick}
                />
            </Box>
            <SignInModalWrapper open={signInModalOpen} onClose={handleSignInModalClose} />
        </>
    )
}

export { SignInPromoBannerWrapper }
export default SignInPromoBannerWrapper
